/**
 * Money / decimal precision helpers (PROMPT §3.1, §18).
 *
 * SQLite `REAL` is binary floating point, so every monetary computation here
 * rounds EXPLICITLY — we never persist or compare un-rounded float drift.
 *
 * Documented policy:
 *   - Bs amounts are the source figures from the flash; stored as REAL rounded
 *     to 2 decimals (`round2`).
 *   - `tasa_bcv` is stored with 4 fixed decimals (`round4`).
 *   - USD amounts are DERIVED (Bs / tasa). For exactness we compute them in
 *     integer cents (`bsToUsdCents`, round-half-up) and expose a 2-decimal
 *     convenience (`bsToUsd`).
 *   - Aggregated USD (month / period) divides the Bs aggregate by the period's
 *     AVERAGE rate (`tasa_prom`), never by a single day's rate (§2.3.8).
 */

/**
 * Round to `decimals` places, half away from zero, correcting binary float
 * error so decimal ties like 1.005 round as a human expects (→ 1.01, and
 * −1.005 → −1.01) rather than as the stored double (→ 1.00). Domain money is
 * non-negative; the symmetric behavior is just for completeness.
 */
export function roundTo(value: number, decimals: number): number {
  if (!Number.isFinite(value)) return value;
  const factor = 10 ** decimals;
  // The (1 + EPSILON) nudge pushes values that sit just below x.5 — due to the
  // binary representation of decimals like 1.005 — up to the .5 boundary.
  const scaled = value * factor * (1 + Number.EPSILON);
  return Math.round(scaled) / factor;
}

/** Round a Bs amount to 2 decimals (storage/display policy for Bs). */
export function round2(value: number): number {
  return roundTo(value, 2);
}

/** Round a rate to 4 decimals (storage policy for `tasa_bcv`). */
export function round4(value: number): number {
  return roundTo(value, 4);
}

/** Convert a decimal amount to integer minor units (cents), round-half-up. */
export function toCents(amount: number): number {
  if (!Number.isFinite(amount)) return 0;
  return Math.round(amount * 100 * (1 + Number.EPSILON));
}

/** Convert integer cents back to a 2-decimal number. */
export function fromCents(cents: number): number {
  return round2(cents / 100);
}

/**
 * Convert a Bs amount to USD cents at a given rate. Returns integer cents.
 * Returns `null` when inputs are missing or the rate is non-positive (never
 * divide-by-zero, never fabricate a USD figure without a rate — §2.3.1).
 */
export function bsToUsdCents(
  bs: number | null | undefined,
  tasa: number | null | undefined,
): number | null {
  if (bs == null || tasa == null || !Number.isFinite(bs) || !Number.isFinite(tasa) || tasa <= 0) {
    return null;
  }
  return toCents(bs / tasa);
}

/** Convert a Bs amount to USD as a 2-decimal number (or null if no valid rate). */
export function bsToUsd(
  bs: number | null | undefined,
  tasa: number | null | undefined,
): number | null {
  const cents = bsToUsdCents(bs, tasa);
  return cents == null ? null : fromCents(cents);
}

/** Safe division returning null instead of NaN/Infinity on zero/invalid denominators. */
export function safeDiv(
  numerator: number | null | undefined,
  denominator: number | null | undefined,
): number | null {
  if (
    numerator == null ||
    denominator == null ||
    !Number.isFinite(numerator) ||
    !Number.isFinite(denominator) ||
    denominator === 0
  ) {
    return null;
  }
  return numerator / denominator;
}

/** Percentage (0..1) of part over whole; null on invalid whole. */
export function ratio(
  part: number | null | undefined,
  whole: number | null | undefined,
): number | null {
  const r = safeDiv(part, whole);
  return r == null ? null : r;
}
